import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { FaPlus, FaBook, FaSave, FaEdit, FaTrash } from 'react-icons/fa';

function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [storybooks, setStorybooks] = useState([]);
  const [storybookCategories, setStorybookCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddStorybookForm, setShowAddStorybookForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingStorybook, setEditingStorybook] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'courses', 'storybooks', 'manual-payments', 'users', 'sales', 'messages'
  const [manualPayments, setManualPayments] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('pending');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [messageFilter, setMessageFilter] = useState('all');
  
  // Pagination states
  const [salesPage, setSalesPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [coursesPage, setCoursesPage] = useState(1);
  const [storybooksPage, setStorybooksPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    titleAr: '',
    descriptionAr: '',
    categoryId: '',
    price: '',
    coverImage: '',
    fileUrl: '',
    fileType: 'pdf',
    pagesCount: '',
    videoExplainerUrl: '',
    presentationUrl: '',
  });
  const [storybookFormData, setStorybookFormData] = useState({
    titleAr: '',
    descriptionAr: '',
    categoryId: '',
    ageRange: '',
    price: '',
    coverImage: '',
    pdfUrl: '',
    pagesCount: '',
  });

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      navigate('/');
      return;
    }
    loadCategories();
    if (activeTab === 'manual-payments') {
      loadManualPayments();
    }
    if (activeTab === 'dashboard') {
      loadAnalytics();
    }
    if (activeTab === 'courses') {
      loadCourses();
    }
    if (activeTab === 'storybooks') {
      loadStorybooks();
      loadStorybookCategories();
    }
    if (activeTab === 'messages') {
      loadContactMessages();
    }
  }, [user, loading, navigate, activeTab, paymentFilter, messageFilter]);

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const data = await apiService.getAdminAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadCourses = async () => {
    try {
      const data = await apiService.getCourses();
      setCourses(data);
    } catch (err) {
      console.error('Error loading courses:', err);
    }
  };

  const loadStorybooks = async () => {
    try {
      const data = await apiService.getStorybooks();
      setStorybooks(data);
    } catch (err) {
      console.error('Error loading storybooks:', err);
    }
  };

  const loadStorybookCategories = async () => {
    try {
      const data = await apiService.getStorybookCategories();
      setStorybookCategories(data);
    } catch (err) {
      console.error('Error loading storybook categories:', err);
    }
  };

  // Storybook management functions (placeholders for future implementation)
  const handleEditStorybook = (storybook) => {
    console.log('Edit storybook:', storybook);
    // TODO: Implement when admin panel supports storybooks
  };

  const handleCancelEditStorybook = () => {
    console.log('Cancel edit storybook');
    // TODO: Implement when admin panel supports storybooks
  };

  const handleDeleteStorybook = async (storybookId, storybookTitle) => {
    console.log('Delete storybook:', storybookId, storybookTitle);
    // TODO: Implement when admin panel supports storybooks
  };

  const handleSubmitStorybook = async (e) => {
    e.preventDefault();
    console.log('Submit storybook');
    // TODO: Implement when admin panel supports storybooks
  };

  const handleStorybookInputChange = (e) => {
    console.log('Storybook input change:', e.target.name);
    // TODO: Implement when admin panel supports storybooks
  };

  const loadContactMessages = async () => {
    try {
      const data = await apiService.getContactMessages(messageFilter);
      setContactMessages(data);
    } catch (err) {
      console.error('Error loading contact messages:', err);
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setFormData({
      titleAr: course.title_ar,
      descriptionAr: course.description_ar,
      categoryId: course.category_id.toString(),
      price: course.price.toString(),
      coverImage: course.cover_image,
      fileUrl: course.file_url,
      fileType: course.file_type,
      pagesCount: course.pages_count.toString(),
      videoExplainerUrl: course.video_explainer_url || '',
      presentationUrl: course.presentation_url || '',
    });
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingCourse(null);
    setFormData({
      titleAr: '',
      descriptionAr: '',
      categoryId: '',
      price: '',
      coverImage: '',
      fileUrl: '',
      fileType: 'pdf',
      pagesCount: '',
      videoExplainerUrl: '',
      presentationUrl: '',
    });
  };

  const handleDeleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`هل أنت متأكد من حذف الدورة "${courseTitle}"?\n\nسيتم حذف جميع البيانات المرتبطة بها.`)) {
      return;
    }

    try {
      await apiService.deleteCourse(courseId);
      alert('تم حذف الدورة بنجاح');
      loadCourses();
    } catch (err) {
      alert(err.message || 'فشل حذف الدورة');
      console.error('Error deleting course:', err);
    }
  };

  const handleUpdateMessageStatus = async (messageId, status) => {
    try {
      await apiService.updateContactMessage(messageId, { status });
      alert('تم تحديث حالة الرسالة');
      loadContactMessages();
    } catch (err) {
      alert(err.message || 'فشل تحديث الرسالة');
      console.error('Error updating message:', err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) {
      return;
    }

    try {
      await apiService.deleteContactMessage(messageId);
      alert('تم حذف الرسالة بنجاح');
      loadContactMessages();
    } catch (err) {
      alert(err.message || 'فشل حذف الرسالة');
      console.error('Error deleting message:', err);
    }
  };

  const handleSelectAllUsers = (checked) => {
    if (checked && analytics && analytics.allUsers) {
      // Select all non-admin users
      const allUserIds = analytics.allUsers
        .filter(u => u.email !== user.email) // Don't select current admin
        .map(u => u.id);
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      alert('الرجاء اختيار مستخدمين للحذف');
      return;
    }

    if (window.confirm(`هل أنت متأكد من حذف ${selectedUsers.length} مستخدم؟\n\nسيتم حذف جميع بياناتهم بما في ذلك المشتريات والتقدم.`)) {
      try {
        let successCount = 0;
        let failCount = 0;
        
        // Delete users one by one with a small delay to avoid race conditions
        for (const userId of selectedUsers) {
          try {
            await apiService.deleteUser(userId);
            successCount++;
            // Small delay to avoid PRAGMA conflicts
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (err) {
            console.error(`Failed to delete user ${userId}:`, err);
            failCount++;
          }
        }
        
        if (failCount === 0) {
          alert(`تم حذف ${successCount} مستخدم بنجاح`);
        } else {
          alert(`تم حذف ${successCount} مستخدم بنجاح\nفشل حذف ${failCount} مستخدم`);
        }
        
        setSelectedUsers([]);
        loadAnalytics(); // Reload data
      } catch (err) {
        alert(err.message || 'فشل حذف بعض المستخدمين');
      }
    }
  };

  const renderPurchaseTypeBadge = (purchase) => {
    const type = purchase.purchase_type || 'individual';
    
    switch(type) {
      case 'subscription':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
            <i className="fas fa-calendar-check"></i>
            <span>اشتراك شهري</span>
          </span>
        );
      case 'bundle':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
            <i className="fas fa-box-open"></i>
            <span>حزمة</span>
          </span>
        );
      case 'individual':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
            <i className="fas fa-shopping-cart"></i>
            <span>محتوى منفرد</span>
          </span>
        );
    }
  };

  // Pagination helper component
  const Pagination = ({ currentPage, totalItems, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (totalPages <= 1) return null;
    
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    
    return (
      <div className="flex items-center justify-center gap-2 mt-6">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg bg-gray-200 text-gray-700 font-bold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <i className="fas fa-chevron-right"></i>
        </button>
        
        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-4 py-2 rounded-lg font-bold transition-all ${
              currentPage === page
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-2 rounded-lg bg-gray-200 text-gray-700 font-bold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <i className="fas fa-chevron-left"></i>
        </button>
      </div>
    );
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await apiService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadManualPayments = async () => {
    try {
      const payments = await apiService.getAdminManualPaymentRequests(paymentFilter);
      setManualPayments(payments);
    } catch (err) {
      console.error('Error loading manual payments:', err);
    }
  };

  const handleApprovePayment = async (requestId) => {
    try {
      await apiService.approveManualPayment(requestId, '');
      loadManualPayments();
    } catch (err) {
      console.error('Approval error:', err);
    }
  };

  const handleRejectPayment = async (requestId) => {
    try {
      await apiService.rejectManualPayment(requestId, '');
      loadManualPayments();
    } catch (err) {
      console.error('Rejection error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titleAr || !formData.descriptionAr || !formData.categoryId || !formData.price || !formData.fileUrl) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setSubmitting(true);
    try {
      const courseData = {
        titleAr: formData.titleAr,
        descriptionAr: formData.descriptionAr,
        categoryId: parseInt(formData.categoryId),
        price: parseFloat(formData.price),
        coverImage: formData.coverImage || null,
        fileUrl: formData.fileUrl,
        fileType: formData.fileType,
        pagesCount: parseInt(formData.pagesCount) || 100,
        videoExplainerUrl: formData.videoExplainerUrl || null,
        presentationUrl: formData.presentationUrl || null,
      };

      if (editingCourse) {
        // Update existing course
        await apiService.updateCourse(editingCourse.id, courseData);
        alert('تم تحديث الدورة بنجاح!');
        handleCancelEdit();
      } else {
        // Add new course
        await apiService.addCourse(courseData);
        alert('تم إضافة الدورة بنجاح!');
        setShowAddForm(false);
      }

      // Reset form and reload courses
      setFormData({
        titleAr: '',
        descriptionAr: '',
        categoryId: '',
        price: '',
        coverImage: '',
        fileUrl: '',
        fileType: 'pdf',
        pagesCount: '',
        videoExplainerUrl: '',
        presentationUrl: '',
      });
      loadCourses();
    } catch (err) {
      alert(err.message || 'فشل حفظ الدورة');
      console.error('Error saving course:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="relative z-10 py-12 md:py-20 bg-gradient-to-b from-bg to-white min-h-screen overflow-x-hidden" dir="rtl">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text mb-1 md:mb-2">
                أهلا وسهلا {user.name}
              </h1>
              <p className="text-sm md:text-lg text-text3">هذي لوحة التحكم تاعك، شوف تقدمك واستمر في التعلم</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 md:gap-4 mb-6 md:mb-8 border-b border-gray-200 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'dashboard'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-chart-line ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">لوحة المعلومات</span>
              <span className="sm:hidden">لوحة</span>
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'courses'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-book ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">محتواي التعليمي</span>
              <span className="sm:hidden">محتواي</span>
              {analytics && analytics.summary.totalBooksOwned > 0 && (
                <span className="inline-block mr-1 md:mr-2 bg-primary text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full">
                  {analytics.summary.totalBooksOwned}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('storybooks')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'storybooks'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-book-open ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">سجل المشتريات</span>
              <span className="sm:hidden">مشتريات</span>
            </button>
            <button
              onClick={() => setActiveTab('manual-payments')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all relative whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'manual-payments'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-money-bill-wave ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">حزمي المشتراة</span>
              <span className="sm:hidden">حزمي</span>
              {manualPayments.filter(p => p.status === 'pending').length > 0 && activeTab !== 'manual-payments' && (
                <span className="absolute -top-1 -left-1 bg-red-500 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full">
                  {manualPayments.filter(p => p.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'users'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-users ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">طلبات قيد المراجعة</span>
              <span className="sm:hidden">طلبات</span>
              {manualPayments.filter(p => p.status === 'pending').length > 0 && (
                <span className="inline-block mr-1 md:mr-2 bg-orange-500 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full">
                  {manualPayments.filter(p => p.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('sales')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'sales'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-shopping-cart ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">سجل جميع المبيعات</span>
              <span className="sm:hidden">مبيعات</span>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-3 md:px-6 py-2 md:py-3 font-bold transition-all relative whitespace-nowrap text-xs md:text-base flex-shrink-0 ${
                activeTab === 'messages'
                  ? 'border-b-4 border-primary text-primary'
                  : 'text-text3 hover:text-primary'
              }`}
            >
              <i className="fas fa-envelope ml-1 md:ml-2 text-sm md:text-base"></i>
              <span className="hidden sm:inline">الرسائل</span>
              <span className="sm:hidden">رسائل</span>
              {contactMessages.filter(m => m.status === 'unread').length > 0 && activeTab !== 'messages' && (
                <span className="absolute -top-1 -left-1 bg-red-500 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full">
                  {contactMessages.filter(m => m.status === 'unread').length}
                </span>
              )}
            </button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {loadingAnalytics ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-text3">جاري تحميل الإحصائيات...</p>
                </div>
              ) : analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                        <i className="fas fa-book text-2xl md:text-3xl opacity-80 mb-2 md:mb-0"></i>
                        <div className="text-right">
                          <div className="text-2xl md:text-4xl font-black">{analytics.summary.totalBooks}</div>
                          <div className="text-xs md:text-sm opacity-90">محتوى مشترى</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                        <i className="fas fa-users text-2xl md:text-3xl opacity-80 mb-2 md:mb-0"></i>
                        <div className="text-right">
                          <div className="text-2xl md:text-4xl font-black">{analytics.summary.totalUsers}</div>
                          <div className="text-xs md:text-sm opacity-90">عدد المشتركين</div>
                          <div className="text-[10px] md:text-xs opacity-75 mt-1">+{analytics.summary.newUsers} الشهر هذا</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                        <i className="fas fa-shopping-cart text-2xl md:text-3xl opacity-80 mb-2 md:mb-0"></i>
                        <div className="text-right">
                          <div className="text-2xl md:text-4xl font-black">{analytics.summary.totalSales}</div>
                          <div className="text-xs md:text-sm opacity-90">عدد المبيعات</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                        <i className="fas fa-dollar-sign text-2xl md:text-3xl opacity-80 mb-2 md:mb-0"></i>
                        <div className="text-right">
                          <div className="text-4xl font-black">{analytics.summary.totalRevenue.toLocaleString()}</div>
                          <div className="text-sm opacity-90">الإيرادات (دج)</div>
                          <div className="text-xs opacity-75 mt-1">متوسط: {analytics.summary.avgOrderValue} دج</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {analytics.summary.pendingPayments > 0 && (
                    <div className="bg-yellow-50 border-r-4 border-yellow-500 p-4 rounded-lg">
                      <div className="flex items-center">
                        <i className="fas fa-exclamation-triangle text-yellow-600 text-2xl ml-3"></i>
                        <div>
                          <p className="font-bold text-yellow-800">لديك {analytics.summary.pendingPayments} طلب دفع يدوي بانتظار المراجعة</p>
                          <button
                            onClick={() => setActiveTab('manual-payments')}
                            className="text-yellow-700 underline text-sm mt-1"
                          >
                            مراجعة الطلبات
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Revenue by Payment Method */}
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                      <h3 className="text-xl font-black text-text mb-4">الإيرادات حسب طريقة الدفع</h3>
                      <div className="space-y-3">
                        {analytics.revenueByMethod.map((method, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-bold text-text">
                                {method.payment_method === 'ccp_manual' 
                                  ? 'CCP يدوي' 
                                  : method.payment_method === 'chargily' || method.payment_method === 'edahabia' || method.payment_method?.startsWith('bundle_')
                                    ? 'دفع أونلاين'
                                    : method.payment_method || 'غير محدد'}
                              </p>
                              <p className="text-sm text-text3">{method.count} عملية</p>
                            </div>
                            <div className="text-left">
                              <p className="text-2xl font-black text-primary">{method.revenue.toLocaleString()}</p>
                              <p className="text-xs text-text3">دج</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Selling Books */}
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                      <h3 className="text-xl font-black text-text mb-4">الدورات الأكثر مبيعاً</h3>
                      <div className="space-y-3">
                        {analytics.topBooks.slice(0, 5).map((book, index) => (
                          <div key={book.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-bold text-text text-sm">{book.title_ar}</p>
                                <p className="text-xs text-text3">{book.sales_count} مبيعات</p>
                              </div>
                            </div>
                            <p className="text-lg font-black text-primary">{book.total_revenue.toLocaleString()} دج</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Recent Purchases */}
                  <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h3 className="text-xl font-black text-text mb-4">آخر المشتريات (آخر 10)</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-right p-3 font-bold text-text">المستخدم</th>
                            <th className="text-right p-3 font-bold text-text">الكتاب</th>
                            <th className="text-right p-3 font-bold text-text">المبلغ</th>
                            <th className="text-right p-3 font-bold text-text">نوع الشراء</th>
                            <th className="text-right p-3 font-bold text-text">طريقة الدفع</th>
                            <th className="text-right p-3 font-bold text-text">التاريخ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.recentPurchases.slice(0, 10).map((purchase, index) => (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="p-3">
                                <p className="font-bold text-text text-sm">{purchase.user_name}</p>
                                <p className="text-xs text-text3">{purchase.user_email}</p>
                              </td>
                              <td className="p-3 text-text2 text-sm">{purchase.course_title}</td>
                              <td className="p-3 font-bold text-primary">{purchase.price} دج</td>
                              <td className="p-3">
                                {renderPurchaseTypeBadge(purchase)}
                              </td>
                              <td className="p-3 text-text3 text-sm">
                                {purchase.payment_method === 'ccp_manual' 
                                  ? 'CCP يدوي' 
                                  : purchase.payment_method === 'chargily' || purchase.payment_method === 'edahabia' || purchase.payment_method?.startsWith('bundle_')
                                    ? 'دفع أونلاين'
                                    : purchase.payment_method || 'غير محدد'}
                              </td>
                              <td className="p-3 text-text3 text-xs">
                                {new Date(purchase.purchased_at).toLocaleString('ar-DZ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-text3">فشل تحميل الإحصائيات</p>
                  <button
                    onClick={loadAnalytics}
                    className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                  >
                    إعادة المحاولة
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && analytics && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-black text-text">جميع المشتركين</h3>
                {selectedUsers.length > 0 && (
                  <button
                    onClick={handleBulkDeleteUsers}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                  >
                    <i className="fas fa-trash"></i>
                    حذف المحدد ({selectedUsers.length})
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-right p-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.length > 0 && selectedUsers.length === analytics.allUsers.filter(u => u.email !== user.email).length}
                          onChange={(e) => handleSelectAllUsers(e.target.checked)}
                          className="w-4 h-4"
                        />
                      </th>
                      <th className="text-right p-3 font-bold text-text">الاسم</th>
                      <th className="text-right p-3 font-bold text-text">البريد الإلكتروني</th>
                      <th className="text-right p-3 font-bold text-text">عدد الدورات المشتراة</th>
                      <th className="text-right p-3 font-bold text-text">تاريخ التسجيل</th>
                      <th className="text-right p-3 font-bold text-text">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.allUsers && analytics.allUsers
                      .slice((usersPage - 1) * itemsPerPage, usersPage * itemsPerPage)
                      .map((userItem) => {
                      const isCurrentAdmin = userItem.email === user.email;
                      return (
                        <tr key={userItem.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-3">
                            {!isCurrentAdmin && (
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(userItem.id)}
                                onChange={(e) => handleSelectUser(userItem.id, e.target.checked)}
                                className="w-4 h-4"
                              />
                            )}
                          </td>
                          <td className="p-3 font-bold text-text text-sm">{userItem.name}</td>
                          <td className="p-3 text-text2 text-sm">{userItem.email}</td>
                          <td className="p-3 font-bold text-center">
                            <div className="flex items-center justify-center gap-2">
                              {/* Subscription count (red - temporary) */}
                              {userItem.has_subscription === 1 && (
                                <span className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700">
                                  {userItem.total_content_count - userItem.permanent_count}
                                  <div className="text-xs">مؤقت</div>
                                </span>
                              )}
                              
                              {/* Show dash if both exist */}
                              {userItem.has_subscription === 1 && userItem.permanent_count > 0 && (
                                <span className="text-gray-400 font-bold">-</span>
                              )}
                              
                              {/* Permanent count (green - forever) */}
                              {userItem.permanent_count > 0 && (
                                <span className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700">
                                  {userItem.permanent_count}
                                  <div className="text-xs">دائم</div>
                                </span>
                              )}
                              
                              {/* Show 0 if no purchases at all */}
                              {userItem.has_subscription === 0 && userItem.permanent_count === 0 && (
                                <span className="text-gray-400">0</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-text3 text-xs">{new Date(userItem.created_at).toLocaleDateString('ar-DZ')}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    const data = await apiService.getAdminUserPurchases(userItem.id);
                                    if (data.purchases.length === 0) {
                                      alert('المستخدم لم يشتري أي دورات بعد');
                                    } else {
                                      alert(`الدورات المشتراة:\n\n${data.purchases.map(p => `- ${p.title_ar} (${p.amount_paid} دج)`).join('\n')}`);
                                    }
                                  } catch (err) {
                                    alert('فشل تحميل الكتب');
                                  }
                                }}
                                className="text-primary hover:text-primary-dark font-bold text-sm px-3 py-1 rounded"
                                disabled={userItem.permanent_count === 0 && userItem.has_subscription === 0}
                              >
                                {(userItem.permanent_count > 0 || userItem.has_subscription === 1) ? 'عرض الدورات' : '-'}
                              </button>
                              {!isCurrentAdmin && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm(`هل أنت متأكد من حذف المستخدم "${userItem.name}"؟\n\nسيتم حذف جميع بياناته بما في ذلك المشتريات والتقدم.`)) {
                                      try {
                                        await apiService.deleteUser(userItem.id);
                                        alert('تم حذف المستخدم بنجاح');
                                        loadAnalytics(); // Reload data
                                      } catch (err) {
                                        alert(err.message || 'فشل حذف المستخدم');
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 font-bold text-sm px-3 py-1 rounded"
                                >
                                  حذف
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination 
                currentPage={usersPage}
                totalItems={analytics.allUsers ? analytics.allUsers.length : 0}
                onPageChange={setUsersPage}
              />
            </div>
          )}

          {/* Sales Tab */}
          {activeTab === 'sales' && analytics && (
            <div className="space-y-8">
              {/* All Sales History */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-2xl font-black text-text mb-6">سجل جميع المبيعات</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-right p-3 font-bold text-text">المستخدم</th>
                        <th className="text-right p-3 font-bold text-text">المحتوى</th>
                        <th className="text-right p-3 font-bold text-text">المبلغ</th>
                        <th className="text-right p-3 font-bold text-text">نوع الشراء</th>
                        <th className="text-right p-3 font-bold text-text">طريقة الدفع</th>
                        <th className="text-right p-3 font-bold text-text">التاريخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recentPurchases
                        .slice((salesPage - 1) * itemsPerPage, salesPage * itemsPerPage)
                        .map((purchase, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-bold text-text text-sm">{purchase.user_name}</p>
                            <p className="text-xs text-text3">{purchase.user_email}</p>
                          </td>
                          <td className="p-3 text-text2 text-sm">{purchase.course_title}</td>
                          <td className="p-3 font-bold text-primary">{purchase.price} دج</td>
                          <td className="p-3">
                            {renderPurchaseTypeBadge(purchase)}
                          </td>
                          <td className="p-3 text-text3 text-sm">
                            {purchase.payment_method === 'ccp_manual' 
                              ? 'CCP يدوي' 
                              : purchase.payment_method === 'chargily' || purchase.payment_method === 'edahabia' || purchase.payment_method?.startsWith('bundle_')
                                ? 'دفع أونلاين'
                                : purchase.payment_method || 'غير محدد'}
                          </td>
                          <td className="p-3 text-text3 text-xs">
                            {new Date(purchase.purchased_at).toLocaleString('ar-DZ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination 
                  currentPage={salesPage}
                  totalItems={analytics.recentPurchases.length}
                  onPageChange={setSalesPage}
                />
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="bg-white rounded-3xl shadow-custom-lg p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-text">رسائل "اتصل بنا"</h2>
                
                {/* Filter */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setMessageFilter('unread')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      messageFilter === 'unread'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    غير مقروءة
                  </button>
                  <button
                    onClick={() => setMessageFilter('read')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      messageFilter === 'read'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    مقروءة
                  </button>
                  <button
                    onClick={() => setMessageFilter('replied')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      messageFilter === 'replied'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    تم الرد عليها
                  </button>
                  <button
                    onClick={() => setMessageFilter('all')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      messageFilter === 'all'
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    الكل
                  </button>
                </div>
              </div>

              {contactMessages.length === 0 ? (
                <div className="text-center py-12">
                  <i className="fas fa-inbox text-6xl text-gray-300 mb-4"></i>
                  <p className="text-text3 text-lg">لا توجد رسائل حالياً</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contactMessages.map((message) => (
                    <div 
                      key={message.id} 
                      className={`border-2 rounded-xl p-6 transition-all ${
                        message.status === 'unread' ? 'border-red-200 bg-red-50' : 
                        message.status === 'replied' ? 'border-green-200 bg-green-50' : 
                        'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-black text-text">{message.name}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              message.status === 'unread' ? 'bg-red-100 text-red-700' :
                              message.status === 'replied' ? 'bg-green-100 text-green-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {message.status === 'unread' ? 'غير مقروءة' :
                               message.status === 'replied' ? 'تم الرد' : 'مقروءة'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-text3 mb-3">
                            <span className="flex items-center gap-1">
                              <i className="fas fa-envelope"></i>
                              {message.email}
                            </span>
                            {message.phone && (
                              <span className="flex items-center gap-1">
                                <i className="fas fa-phone"></i>
                                {message.phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <i className="fas fa-clock"></i>
                              {new Date(message.created_at).toLocaleString('ar-DZ')}
                            </span>
                          </div>
                          <div className="bg-white p-4 rounded-lg mb-3">
                            <p className="font-bold text-text mb-2">الموضوع: {message.subject}</p>
                            <p className="text-text2 whitespace-pre-wrap">{message.message}</p>
                          </div>
                          {message.admin_notes && (
                            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                              <p className="text-sm text-gray-700">
                                <strong>ملاحظات الأدمن:</strong> {message.admin_notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 pt-4 border-t border-gray-200">
                        {message.status === 'unread' && (
                          <button
                            onClick={() => handleUpdateMessageStatus(message.id, 'read')}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2"
                          >
                            <i className="fas fa-check"></i>
                            تعليم كمقروءة
                          </button>
                        )}
                        {message.status !== 'replied' && (
                          <button
                            onClick={() => handleUpdateMessageStatus(message.id, 'replied')}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                          >
                            <i className="fas fa-reply"></i>
                            تم الرد
                          </button>
                        )}
                        <a
                          href={`mailto:${message.email}?subject=Re: ${message.subject}`}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all flex items-center gap-2"
                        >
                          <i className="fas fa-envelope"></i>
                          رد عبر البريد
                        </a>
                        <button
                          onClick={() => handleDeleteMessage(message.id)}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2 mr-auto"
                        >
                          <i className="fas fa-trash"></i>
                          حذف
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <>
              <div className="flex justify-end mb-8">
                <button
                  onClick={() => {
                    setShowAddForm(!showAddForm);
                    if (editingCourse) handleCancelEdit();
                  }}
                  className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 flex items-center gap-2 shadow-lg"
                >
                  <FaPlus />
                  {showAddForm ? 'إلغاء' : 'إضافة دورة تفاعلية'}
                </button>
              </div>

          {/* Add/Edit Form */}
          {(showAddForm || editingCourse) && (
            <div className="bg-white rounded-3xl shadow-custom-lg p-8 mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <FaBook className="text-primary text-2xl" />
                  <h2 className="text-2xl font-black text-text">
                    {editingCourse ? 'تعديل الدورة التفاعلية' : 'إضافة دورة تفاعلية جديدة'}
                  </h2>
                </div>
                {editingCourse && (
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <i className="fas fa-times text-2xl"></i>
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      عنوان الدورة <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="titleAr"
                      value={formData.titleAr}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      القسم <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      required
                    >
                      <option value="">اختر القسم</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name_ar}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      السعر (دج) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      عدد الصفحات
                    </label>
                    <input
                      type="number"
                      name="pagesCount"
                      value={formData.pagesCount}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      نوع الملف <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="fileType"
                      value={formData.fileType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      required
                    >
                      <option value="pdf">PDF</option>
                      <option value="html">HTML</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      رابط صورة الغلاف
                    </label>
                    <input
                      type="url"
                      name="coverImage"
                      value={formData.coverImage}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    الوصف <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="descriptionAr"
                    value={formData.descriptionAr}
                    onChange={handleInputChange}
                    rows="5"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    required
                  ></textarea>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    رابط الملف <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    name="fileUrl"
                    value={formData.fileUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/book.pdf"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    required
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    رابط مباشر لملف PDF أو HTML (يمكن استخدام Cloudflare R2 أو أي خدمة استضافة)
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    رابط الفيديو التوضيحي (اختياري)
                  </label>
                  <input
                    type="url"
                    name="videoExplainerUrl"
                    value={formData.videoExplainerUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/video-explainer"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    رابط مباشر للفيديو التوضيحي التفاعلي (YouTube embed, Vimeo, etc.)
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    رابط العرض التقديمي (اختياري)
                  </label>
                  <input
                    type="url"
                    name="presentationUrl"
                    value={formData.presentationUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/presentation"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    رابط مباشر للعرض التقديمي (Google Slides embed, SlideShare, etc.)
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  <FaSave />
                  {submitting ? 'جاري الحفظ...' : 'حفظ الدورة'}
                </button>
              </form>
            </div>
          )}

          {/* Courses List */}
          <div className="bg-white rounded-3xl shadow-custom-lg p-8">
            <h2 className="text-2xl font-black text-text mb-6">الدورات الرقمية التفاعلية المتاحة</h2>
            
            {courses.length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-book text-6xl text-gray-300 mb-4"></i>
                <p className="text-text3 text-lg">لا توجد دورات حالياً</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {courses
                  .slice((coursesPage - 1) * itemsPerPage, coursesPage * itemsPerPage)
                  .map((course) => (
                  <div key={course.id} className="border-2 border-gray-200 rounded-xl p-6 hover:border-primary transition-all">
                    <div className="flex items-start gap-6">
                      {/* Course Cover */}
                      <img 
                        src={course.cover_image} 
                        alt={course.title_ar}
                        className="w-32 h-40 object-cover rounded-lg shadow-md"
                      />
                      
                      {/* Course Info */}
                      <div className="flex-1">
                        <h3 className="text-xl font-black text-text mb-2">{course.title_ar}</h3>
                        <p className="text-text3 mb-3 line-clamp-2">{course.description_ar}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-text3 mb-4">
                          <span className="flex items-center gap-1">
                            <i className="fas fa-folder"></i>
                            {categories.find(c => c.id === course.category_id)?.name_ar || 'غير محدد'}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="fas fa-file-alt"></i>
                            {course.pages_count} صفحة
                          </span>
                          <span className="font-bold text-primary">{course.price} دج</span>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-3">
                          <button
                            onClick={() => navigate(`/course/${course.id}`)}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2"
                          >
                            <i className="fas fa-eye"></i>
                            معاينة
                          </button>
                          <button
                            onClick={() => {
                              handleEditCourse(course);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all flex items-center gap-2"
                          >
                            <FaEdit />
                            تعديل
                          </button>
                          <button
                            onClick={() => handleDeleteCourse(course.id, course.title_ar)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
                          >
                            <FaTrash />
                            حذف
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Pagination 
              currentPage={coursesPage}
              totalItems={courses.length}
              onPageChange={setCoursesPage}
            />
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-gray-700">
              <strong>ملاحظة:</strong> يمكنك إضافة دورات تفاعلية جديدة أو إدارة الدورات الموجودة من هذه الصفحة.
            </p>
          </div>
            </>
          )}

          {/* Storybooks Tab */}
          {activeTab === 'storybooks' && (
            <>
              <div className="flex justify-end mb-8">
                <button
                  onClick={() => {
                    setShowAddStorybookForm(!showAddStorybookForm);
                    if (editingStorybook) handleCancelEditStorybook();
                  }}
                  className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 flex items-center gap-2 shadow-lg"
                >
                  <FaPlus />
                  {showAddStorybookForm ? 'إلغاء' : 'إضافة قصة مصورة'}
                </button>
              </div>

              {/* Add/Edit Storybook Form */}
              {(showAddStorybookForm || editingStorybook) && (
                <div className="bg-white rounded-3xl shadow-custom-lg p-8 mb-12">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <i className="fas fa-book-open text-primary text-2xl"></i>
                      <h2 className="text-2xl font-black text-text">
                        {editingStorybook ? 'تعديل القصة المصورة' : 'إضافة قصة مصورة جديدة'}
                      </h2>
                    </div>
                    {editingStorybook && (
                      <button
                        onClick={handleCancelEditStorybook}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <i className="fas fa-times text-2xl"></i>
                      </button>
                    )}
                  </div>

                  <form onSubmit={handleSubmitStorybook} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 font-bold mb-2">
                          عنوان القصة <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="titleAr"
                          value={storybookFormData.titleAr}
                          onChange={handleStorybookInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-2">
                          الفئة العمرية
                        </label>
                        <select
                          name="ageRange"
                          value={storybookFormData.ageRange}
                          onChange={handleStorybookInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        >
                          <option value="">اختر الفئة العمرية</option>
                          <option value="3-5">3-5 سنوات</option>
                          <option value="6-8">6-8 سنوات</option>
                          <option value="9-12">9-12 سنة</option>
                          <option value="13+">13+ سنة</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-2">
                          القسم
                        </label>
                        <select
                          name="categoryId"
                          value={storybookFormData.categoryId}
                          onChange={handleStorybookInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        >
                          <option value="">اختر القسم</option>
                          {storybookCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name_ar}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-2">
                          السعر (دج) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="price"
                          value={storybookFormData.price}
                          onChange={handleStorybookInputChange}
                          min="0"
                          step="0.01"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-2">
                          عدد الصفحات
                        </label>
                        <input
                          type="number"
                          name="pagesCount"
                          value={storybookFormData.pagesCount}
                          onChange={handleStorybookInputChange}
                          min="0"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-2">
                          رابط صورة الغلاف
                        </label>
                        <input
                          type="url"
                          name="coverImage"
                          value={storybookFormData.coverImage}
                          onChange={handleStorybookInputChange}
                          placeholder="https://example.com/image.jpg"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-bold mb-2">
                        الوصف <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="descriptionAr"
                        value={storybookFormData.descriptionAr}
                        onChange={handleStorybookInputChange}
                        rows="5"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        required
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-bold mb-2">
                        رابط ملف PDF <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        name="pdfUrl"
                        value={storybookFormData.pdfUrl}
                        onChange={handleStorybookInputChange}
                        placeholder="https://example.com/storybook.pdf"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        required
                      />
                      <p className="text-sm text-gray-600 mt-2">
                        رابط مباشر لملف PDF للقصة المصورة
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-primary text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
                    >
                      <FaSave />
                      {submitting ? 'جاري الحفظ...' : 'حفظ القصة'}
                    </button>
                  </form>
                </div>
              )}

              {/* Storybooks List */}
              <div className="bg-white rounded-3xl shadow-custom-lg p-6 md:p-8">
                <h2 className="text-2xl font-black text-text mb-6">القصص المصورة المتاحة</h2>
                
                {storybooks.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="fas fa-book-open text-6xl text-gray-300 mb-4"></i>
                    <p className="text-text3 text-lg">لا توجد قصص مصورة حالياً</p>
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {storybooks
                      .slice((storybooksPage - 1) * itemsPerPage, storybooksPage * itemsPerPage)
                      .map((storybook) => (
                      <div key={storybook.id} className="border-2 border-gray-200 rounded-xl p-6 hover:border-primary transition-all">
                        <div className="flex items-start gap-6">
                          {/* Storybook Cover */}
                          <img 
                            src={storybook.cover_image} 
                            alt={storybook.title_ar}
                            className="w-32 h-40 object-cover rounded-lg shadow-md"
                          />
                          
                          {/* Storybook Info */}
                          <div className="flex-1">
                            <h3 className="text-xl font-black text-text mb-2">{storybook.title_ar}</h3>
                            <p className="text-text3 mb-3 line-clamp-2">{storybook.description_ar}</p>
                            
                            <div className="flex items-center gap-4 text-sm text-text3 mb-4">
                              {storybook.age_range && (
                                <span className="flex items-center gap-1">
                                  <i className="fas fa-child"></i>
                                  {storybook.age_range} سنوات
                                </span>
                              )}
                              {storybook.pages_count && (
                                <span className="flex items-center gap-1">
                                  <i className="fas fa-file-alt"></i>
                                  {storybook.pages_count} صفحة
                                </span>
                              )}
                              <span className="font-bold text-primary">{storybook.price} دج</span>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-3">
                              <button
                                onClick={() => navigate(`/storybooks/${storybook.id}`)}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2"
                              >
                                <i className="fas fa-eye"></i>
                                معاينة
                              </button>
                              <button
                                onClick={() => {
                                  handleEditStorybook(storybook);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all flex items-center gap-2"
                              >
                                <FaEdit />
                                تعديل
                              </button>
                              <button
                                onClick={() => handleDeleteStorybook(storybook.id, storybook.title_ar)}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
                              >
                                <FaTrash />
                                حذف
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Pagination 
                  currentPage={storybooksPage}
                  totalItems={storybooks.length}
                  onPageChange={setStorybooksPage}
                />
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-gray-700">
                  <strong>ملاحظة:</strong> يمكنك إضافة قصص مصورة جديدة أو إدارة القصص الموجودة من هذه الصفحة.
                </p>
              </div>
            </>
          )}

          {/* Manual Payments Tab */}
          {activeTab === 'manual-payments' && (
            <div className="bg-white rounded-3xl shadow-custom-lg p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-text">طلبات الدفع اليدوي عبر CCP</h2>
                
                {/* Filter */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setPaymentFilter('pending')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      paymentFilter === 'pending'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    قيد المراجعة
                  </button>
                  <button
                    onClick={() => setPaymentFilter('approved')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      paymentFilter === 'approved'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    موافق عليها
                  </button>
                  <button
                    onClick={() => setPaymentFilter('rejected')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      paymentFilter === 'rejected'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    مرفوضة
                  </button>
                  <button
                    onClick={() => setPaymentFilter('all')}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      paymentFilter === 'all'
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    الكل
                  </button>
                </div>
              </div>

              {manualPayments.length === 0 ? (
                <div className="text-center py-12">
                  <i className="fas fa-inbox text-6xl text-gray-300 mb-4"></i>
                  <p className="text-text3 text-lg">لا توجد طلبات دفع يدوي حالياً</p>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-right p-4 font-bold text-text">الطالب</th>
                        <th className="text-right p-4 font-bold text-text">المحتوى</th>
                        <th className="text-right p-4 font-bold text-text">المبلغ</th>
                        <th className="text-right p-4 font-bold text-text">اسم المحول</th>
                        <th className="text-right p-4 font-bold text-text">الهاتف</th>
                        <th className="text-right p-4 font-bold text-text">التاريخ</th>
                        <th className="text-right p-4 font-bold text-text">الإيصال</th>
                        <th className="text-right p-4 font-bold text-text">الحالة</th>
                        <th className="text-right p-4 font-bold text-text">الإجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualPayments
                        .slice((paymentsPage - 1) * itemsPerPage, paymentsPage * itemsPerPage)
                        .map((payment) => (
                        <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-4">
                            <div className="font-bold text-text">{payment.user_name}</div>
                            <div className="text-sm text-text3">{payment.user_email}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-text2">{payment.course_title}</div>
                            {payment.purchase_type === 'bundle' && payment.item_ids && (
                              <div className="text-xs text-primary font-bold mt-1">
                                <i className="fas fa-boxes mr-1"></i>
                                حزمة ({JSON.parse(payment.item_ids).length} محتويات)
                              </div>
                            )}
                            {payment.purchase_type === 'subscription' && (
                              <div className="text-xs text-purple-600 font-bold mt-1">
                                <i className="fas fa-calendar-alt mr-1"></i>
                                اشتراك شهري
                              </div>
                            )}
                            {(!payment.purchase_type || payment.purchase_type === 'individual') && (
                              <div className="text-xs text-gray-500 mt-1">
                                <i className="fas fa-book mr-1"></i>
                                شراء فردي
                              </div>
                            )}
                          </td>
                          <td className="p-4 font-bold text-primary">{payment.amount} دج</td>
                          <td className="p-4 text-text2">{payment.ccp_sender_name}</td>
                          <td className="p-4 text-text3 font-mono">{payment.phone}</td>
                          <td className="p-4 text-text3 text-sm">
                            {new Date(payment.created_at).toLocaleDateString('ar-DZ')}
                          </td>
                          <td className="p-4">
                            {payment.payment_proof_url ? (
                              <button
                                onClick={() => setSelectedReceipt(payment.payment_proof_url)}
                                className="text-primary hover:text-primary-dark font-bold"
                              >
                                <i className="fas fa-image ml-1"></i>
                                عرض
                              </button>
                            ) : (
                              <span className="text-text4">لا يوجد</span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              payment.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {payment.status === 'pending' ? 'قيد المراجعة' :
                               payment.status === 'approved' ? 'موافق عليه' : 'مرفوض'}
                            </span>
                          </td>
                          <td className="p-4">
                            {payment.status === 'pending' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleApprovePayment(payment.id)}
                                  className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold text-sm"
                                >
                                  <i className="fas fa-check ml-1"></i>
                                  موافقة
                                </button>
                                <button
                                  onClick={() => handleRejectPayment(payment.id)}
                                  className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 font-bold text-sm"
                                >
                                  <i className="fas fa-times ml-1"></i>
                                  رفض
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination 
                  currentPage={paymentsPage}
                  totalItems={manualPayments.length}
                  onPageChange={setPaymentsPage}
                />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {selectedReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReceipt(null)}>
          <div className="bg-white rounded-2xl p-4 max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-text">الإيصال</h3>
              <button
                onClick={() => setSelectedReceipt(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <img src={selectedReceipt} alt="Receipt" className="w-full h-auto rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
